import React, { useEffect, useMemo, useRef, useState } from "react";
import { onBackdropMouseDown, onModalPanelMouseDown } from "../lib/modal";
import { api, API_BASE } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, QrCode, MessageCircle, UsersRound, Key, Check, X, RefreshCw, Copy, ShieldOff, ShieldCheck, History, Upload, Download } from "lucide-react";
import { Link } from "react-router-dom";
import EmptyState from "@/components/EmptyState";
import { localizeError } from "@/lib/errors";
import { useConfirmDialog } from "@/hooks/useConfirmDialog";
import { resolveNewCustomerPhoneDuplicate } from "@/lib/customerPhoneDuplicate";

const empty = { name: "", phone: "", email: "", language_pref: "ar", notes: "", accepts_marketing: false, date_of_birth: "" };

export default function Customers() {
  const { t, lang } = useLanguage();
  const { confirm, ConfirmDialogSlot } = useConfirmDialog();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [broadcast, setBroadcast] = useState("");
  const [search, setSearch] = useState("");

  // Customer credentials modal
  const [credFor, setCredFor] = useState(null); // customer object
  const [credForm, setCredForm] = useState({ email: "", password: "" });
  const [credInfo, setCredInfo] = useState({ has_password: false, email: "" });
  const [credSaving, setCredSaving] = useState(false);
  const [generatedPwd, setGeneratedPwd] = useState(""); // shown once after reset
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const [historyFor, setHistoryFor] = useState(null);
  const [historyData, setHistoryData] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [selectedIds, setSelectedIds] = useState([]);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);

  const load = async () => { const r = await api.get("/customers"); setItems(r.data); };
  useEffect(() => { load(); }, []);
  useEffect(() => { setPage(1); setSelectedIds([]); }, [search]);

  const submit = async () => {
    try {
      const payload = {
        name: form.name,
        phone: form.phone,
        email: form.email || "",
        language_pref: form.language_pref || "ar",
        notes: form.notes || "",
        accepts_marketing: Boolean(form.accepts_marketing),
        date_of_birth: form.date_of_birth || null,
      };
      if (editId) {
        await api.put(`/customers/${editId}`, payload);
      } else {
        let allowDuplicate = false;
        try {
          const dup = await resolveNewCustomerPhoneDuplicate({
            api,
            phone: form.phone,
            lang,
            confirm,
          });
          if (!dup.proceed) return;
          allowDuplicate = dup.allowDuplicate;
        } catch (e) {
          toast.error(localizeError(e, t, lang));
          return;
        }
        await api.post("/customers", payload, {
          params: allowDuplicate ? { allow_duplicate: true } : {},
        });
      }
      toast.success(t("saved")); setOpen(false); setForm(empty); setEditId(null); load();
    } catch { toast.error(t("error_generic")); }
  };

  const remove = async (id) => {
    const ok = await confirm({
      title: "Delete?",
      confirmLabel: lang === "ar" ? "حذف" : "Delete",
      cancelLabel: t("cancel"),
      variant: "danger",
    });
    if (!ok) return;
    await api.delete(`/customers/${id}`); load();
  };

  // Credentials handlers
  const openCredentials = async (c) => {
    setCredFor(c);
    setCredForm({ email: "", password: "" });
    setGeneratedPwd("");
    setConfirmRevoke(false);
    try {
      const r = await api.get(`/customers/${c.customer_id}/credentials`);
      setCredInfo(r.data);
      setCredForm({ email: r.data.email || "", password: "" });
    } catch {
      setCredInfo({ has_password: false, email: "" });
    }
  };
  const closeCredentials = () => {
    setCredFor(null); setGeneratedPwd(""); setConfirmRevoke(false);
  };
  const saveCredentials = async () => {
    const payload = {};
    if (credForm.email && credForm.email !== credInfo.email) payload.email = credForm.email;
    if (credForm.password) payload.password = credForm.password;
    if (Object.keys(payload).length === 0) { toast.info(lang === "ar" ? "لا تغييرات" : "No changes"); return; }
    setCredSaving(true);
    try {
      const r = await api.post(`/customers/${credFor.customer_id}/credentials`, payload);
      setCredInfo({ email: r.data.email, has_password: r.data.has_password });
      setCredForm({ email: r.data.email, password: "" });
      toast.success(lang === "ar" ? "تم الحفظ" : "Saved");
    } catch (e) {
      toast.error(localizeError(e, t, lang));
    } finally { setCredSaving(false); }
  };
  const resetPassword = async () => {
    setCredSaving(true);
    try {
      const r = await api.post(`/customers/${credFor.customer_id}/reset-password`, {});
      setGeneratedPwd(r.data.new_password);
      setCredInfo(prev => ({ ...prev, has_password: true }));
      toast.success(lang === "ar" ? "تم إنشاء كلمة مرور جديدة" : "New password generated");
    } catch (e) {
      toast.error(localizeError(e, t, lang));
    } finally { setCredSaving(false); }
  };
  const revokeCredentials = async () => {
    setCredSaving(true);
    try {
      await api.delete(`/customers/${credFor.customer_id}/credentials`);
      setCredInfo(prev => ({ ...prev, has_password: false }));
      setConfirmRevoke(false);
      toast.success(lang === "ar" ? "تم إلغاء صلاحية الدخول" : "Login revoked");
    } catch (e) {
      toast.error(localizeError(e, t, lang));
    } finally { setCredSaving(false); }
  };
  const copyPwd = async (pwd) => {
    try {
      await navigator.clipboard.writeText(pwd);
      toast.success(lang === "ar" ? "تم النسخ" : "Copied");
    } catch {
      toast.error(t("copy_failed"));
    }
  };

  const openHistory = async (c) => {
    setHistoryFor(c);
    setHistoryLoading(true);
    setHistoryData(null);
    try {
      const r = await api.get(`/customers/${c.customer_id}/history`);
      setHistoryData(r.data);
    } catch (e) {
      toast.error(localizeError(e, t, lang));
      setHistoryFor(null);
    } finally {
      setHistoryLoading(false);
    }
  };

  const closeHistory = () => {
    setHistoryFor(null);
    setHistoryData(null);
    setHistoryLoading(false);
  };

  const formatHistoryDate = (value) => {
    if (!value) return "—";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return new Intl.DateTimeFormat(lang === "ar" ? "ar-EG" : "en-US", {
      year: "numeric", month: "short", day: "numeric",
    }).format(d);
  };

  const lastUsedServices = useMemo(() => {
    const entries = [];
    (historyData?.invoices || []).forEach((inv) => {
      (inv.items || []).forEach((it) => {
        entries.push({
          name: it.name || it.service_name || it.product_name || it.package_name || it.item_name || (lang === "ar" ? "عنصر" : "Item"),
          date: inv.created_at,
          source: "invoice",
        });
      });
    });
    (historyData?.bookings || []).forEach((bk) => {
      (bk.items || []).forEach((it) => {
        entries.push({
          name: it.name || it.service_name || it.product_name || it.package_name || it.item_name || (lang === "ar" ? "عنصر" : "Item"),
          date: bk.created_at,
          source: "booking",
        });
      });
    });
    return entries
      .filter((entry) => entry.name)
      .sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0))
      .slice(0, 4);
  }, [historyData, lang]);

  const waLink = (phone, message) => {
    const p = phone.replace(/[^0-9]/g, "");
    return `https://wa.me/${p}?text=${encodeURIComponent(message)}`;
  };

  const sendBroadcast = () => {
    if (!broadcast.trim()) { toast.error(t("required_field")); return; }
    items.forEach(c => {
      if (c.phone) window.open(waLink(c.phone, broadcast), "_blank");
    });
  };

  const filtered = useMemo(() => items.filter(c => !search || (c.name || "").toLowerCase().includes(search.toLowerCase()) || (c.phone || "").includes(search)), [items, search]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageItems = useMemo(() => filtered.slice((page - 1) * pageSize, page * pageSize), [filtered, page, pageSize]);
  const isEmpty = items.length === 0;

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const downloadTemplate = async () => {
    try {
      const r = await fetch(`${API_BASE}/customers/template`, { credentials: "include" });
      if (!r.ok) throw new Error();
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "customers_template.xlsx";
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      toast.error(t("error_generic"));
    }
  };

  const onFile = async (ev) => {
    const f = ev.target.files?.[0];
    if (!f || importing) return;
    const fd = new FormData();
    fd.append("file", f, f.name);
    setImporting(true);
    try {
      const r = await api.post("/customers/import", fd, { timeout: 10 * 60 * 1000 });
      toast.success(`${r.data.created} ${t("imported_count")}, ${r.data.skipped} ${t("skipped_count")}`);
      load();
    } catch (e) {
      toast.error(localizeError(e, t, lang));
    } finally {
      if (fileRef.current) fileRef.current.value = "";
      setImporting(false);
    }
  };

  const toggleSelection = (id) => {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const toggleSelectVisible = () => {
    const visibleIds = pageItems.map((c) => c.customer_id);
    const allVisibleSelected = visibleIds.every((id) => selectedIds.includes(id));
    setSelectedIds((prev) => {
      if (allVisibleSelected) return prev.filter((id) => !visibleIds.includes(id));
      return Array.from(new Set([...prev, ...visibleIds]));
    });
  };

  const removeSelected = async () => {
    if (!selectedIds.length) return;
    const ok = await confirm({
      title: lang === "ar" ? "حذف العملاء المحددين؟" : "Delete the selected customers?",
      confirmLabel: lang === "ar" ? "حذف" : "Delete",
      cancelLabel: t("cancel"),
      variant: "danger",
    });
    if (!ok) return;
    try {
      for (const id of selectedIds) {
        await api.delete(`/customers/${id}`);
      }
      toast.success(lang === "ar" ? "تم حذف العملاء المحددين" : "Selected customers deleted");
      setSelectedIds([]);
      load();
    } catch (e) {
      toast.error(localizeError(e, t, lang));
    }
  };

  return (
    <div className="space-y-6" data-testid="customers-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl gold-text">{t("nav_customers")}</h1>
          <p className="text-sm text-[var(--text-muted)] mt-1">{t("ar") === "ar" || true ? "" : ""}</p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <button onClick={downloadTemplate} data-testid="download-customer-template-btn" className="btn-rose-outline px-3 py-2 rounded-md text-sm flex items-center gap-2">
            <Download size={14}/>{t("download_template")}
          </button>
          <input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={onFile} className="hidden" data-testid="customer-import-file"/>
          <button onClick={() => fileRef.current?.click()} data-testid="import-customer-btn" className="btn-rose-outline px-3 py-2 rounded-md text-sm flex items-center gap-2 disabled:opacity-60" disabled={importing} aria-busy={importing}>
            {importing ? <RefreshCw size={14} className="animate-spin" /> : <Upload size={14}/>} 
            {importing ? (lang === "ar" ? "جاري الاستيراد..." : "Importing...") : t("import_excel")}
          </button>
          <button onClick={() => { setForm(empty); setEditId(null); setOpen(true); }} data-testid="add-customer-btn" className="btn-gold px-4 py-2 rounded-md flex items-center gap-2 text-sm"><Plus size={16}/>{t("add")}</button>
        </div>
      </div>

      {isEmpty ? (
        <EmptyState
          icon={UsersRound}
          title={t("no_data")}
          description={lang === "ar" ? "ابدأ بإضافة أول عميل لإدارة الحجوزات والولاء وتاريخ المشتريات." : "Add your first customer to manage bookings, loyalty, and purchase history."}
          actionLabel={t("add")}
          onAction={() => { setForm(empty); setEditId(null); setOpen(true); }}
          testId="customers-empty"
        />
      ) : (
        <>
          <div className="card-surface p-5 space-y-3">
            <label className="text-xs text-[var(--text-muted)] font-semibold uppercase tracking-wider">{t("whatsapp_marketing")}</label>
            <div className="flex gap-2">
              <input className="input-dark flex-1" placeholder={t("marketing_message")} value={broadcast} onChange={e => setBroadcast(e.target.value)} data-testid="broadcast-input"/>
              <button onClick={sendBroadcast} data-testid="broadcast-send-btn" className="btn-rose-outline px-4 py-2 rounded-md flex items-center gap-2"><MessageCircle size={16}/>{t("send")}</button>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <input className="input-dark max-w-sm" placeholder={t("search")} value={search} onChange={e => setSearch(e.target.value)}/>
            <div className="flex flex-wrap items-center gap-2">
              <button onClick={removeSelected} className="btn-rose-outline px-3 py-2 rounded-md text-sm flex items-center gap-2" disabled={!selectedIds.length}>
                {lang === "ar" ? "حذف المحدد" : "Delete selected"}
              </button>
              <div className="text-sm text-[var(--text-muted)]">
                {lang === "ar" ? `عرض ${Math.min((page - 1) * pageSize + 1, filtered.length)}-${Math.min(page * pageSize, filtered.length)} من ${filtered.length}` : `Showing ${Math.min((page - 1) * pageSize + 1, filtered.length)}-${Math.min(page * pageSize, filtered.length)} of ${filtered.length}`}
              </div>
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="btn-rose-outline px-3 py-2 rounded-md text-sm disabled:opacity-50">{lang === "ar" ? "السابق" : "Prev"}</button>
              <span className="text-sm text-[var(--text-muted)]">{page}/{totalPages}</span>
              <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="btn-rose-outline px-3 py-2 rounded-md text-sm disabled:opacity-50">{lang === "ar" ? "التالي" : "Next"}</button>
            </div>
          </div>

          <div className="card-surface overflow-auto">
            <table className="table-dark">
              <thead><tr>
                <th className="w-10">
                  <input type="checkbox" checked={pageItems.length > 0 && pageItems.every((c) => selectedIds.includes(c.customer_id))} onChange={toggleSelectVisible} className="h-4 w-4 rounded border-[var(--border)] bg-transparent" />
                </th>
                <th>{t("name")}</th><th>{t("phone")}</th><th>{t("membership_tier")}</th><th>{t("loyalty_points")}</th><th>{t("total_spent")}</th><th>{t("actions")}</th>
              </tr></thead>
              <tbody>
                {pageItems.map(c => (
                  <tr key={c.customer_id}>
                    <td><input type="checkbox" checked={selectedIds.includes(c.customer_id)} onChange={() => toggleSelection(c.customer_id)} className="h-4 w-4 rounded border-[var(--border)] bg-transparent" /></td>
                    <td className="text-[var(--text)] font-medium">{c.name}</td>
                    <td className="text-[var(--text-muted)]" dir="ltr">{c.phone}</td>
                    <td><span className={`badge tier-${c.membership_tier}`}>{c.membership_tier}</span></td>
                    <td className="gold-text font-medium num-display">{c.loyalty_points}</td>
                    <td className="text-[var(--text)] num-display">{c.total_spent.toFixed(2)}</td>
                    <td>
                      <div className="flex gap-1.5">
                        <Link to={`/card/${c.customer_id}`} target="_blank" className="p-1.5 rounded border border-[var(--border)] hover:border-[var(--gold)] text-[var(--text-muted)] hover:text-[var(--gold)] transition"><QrCode size={14}/></Link>
                        {c.phone && <a href={waLink(c.phone, lang === "ar" ? `مرحبا ${c.name}` : `Hello ${c.name}`)} target="_blank" rel="noreferrer" className="p-1.5 rounded border border-[var(--border)] hover:border-green-500 text-[var(--text-muted)] hover:text-green-500 transition"><MessageCircle size={14}/></a>}
                        <button onClick={() => openCredentials(c)} data-testid={`customer-credentials-${c.customer_id}`} title={lang === "ar" ? "بيانات الدخول" : "Login credentials"} className="p-1.5 rounded border border-[var(--border)] hover:border-blue-500 text-[var(--text-muted)] hover:text-blue-400 transition"><Key size={14}/></button>
                        <button onClick={() => openHistory(c)} className="p-1.5 rounded border border-[var(--border)] hover:border-[#C9922F] text-[var(--text-muted)] hover:text-[#C9922F] transition" title={lang === "ar" ? "السجل" : "History"}><History size={14}/></button>
                        <button onClick={() => { setForm({ ...empty, ...c, date_of_birth: c.date_of_birth || c.birthday || "" }); setEditId(c.customer_id); setOpen(true); }} className="p-1.5 rounded border border-[var(--border)] hover:border-[var(--gold)] text-[var(--text-muted)] hover:text-[var(--gold)] transition"><Pencil size={14}/></button>
                        <button onClick={() => remove(c.customer_id)} className="p-1.5 rounded border border-[var(--border)] hover:border-[var(--error)] text-[var(--text-muted)] hover:text-[var(--error)] transition"><Trash2 size={14}/></button>
                      </div>
                    </td>
                  </tr>
                ))}
                {pageItems.length === 0 && <tr><td colSpan={8} className="text-center text-[var(--text-muted)] py-12">{t("no_data")}</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}

      {open && (
        <div className="fixed inset-0 z-50 bg-[#2B1C1C]/30 backdrop-blur-sm flex items-center justify-center p-4" onMouseDown={(e) => onBackdropMouseDown(e, () => setOpen(false))}>
          <div className="card-surface p-6 w-full max-w-lg space-y-4" onMouseDown={onModalPanelMouseDown}>
            <h2 className="font-heading text-2xl gold-text">{editId ? t("edit") : t("add")}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2"><label className="text-xs text-[var(--text-muted)]">{t("name")}</label><input className="input-dark" data-testid="customer-name" value={form.name} onChange={e => setForm({...form, name: e.target.value})}/></div>
              <div><label className="text-xs text-[var(--text-muted)]">{t("phone")}</label><input className="input-dark" data-testid="customer-phone" placeholder="+974 5XXX XXXX" dir="ltr" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})}/></div>
              <div><label className="text-xs text-[var(--text-muted)]">{t("email")}</label><input className="input-dark" value={form.email} onChange={e => setForm({...form, email: e.target.value})}/></div>
              <div><label className="text-xs text-[var(--text-muted)]">{t("date_of_birth")}</label><input type="date" className="input-dark" data-testid="customer-dob" value={form.date_of_birth || ""} onChange={e => setForm({...form, date_of_birth: e.target.value})}/></div>
              <div><label className="text-xs text-[var(--text-muted)]">{t("language")}</label>
                <select className="input-dark" value={form.language_pref} onChange={e => setForm({...form, language_pref: e.target.value})}>
                  <option value="ar">العربية</option><option value="en">English</option>
                </select>
              </div>
              <div className="md:col-span-2"><label className="text-xs text-[var(--text-muted)]">{t("notes")}</label><textarea className="input-dark" rows={2} value={form.notes} onChange={e => setForm({...form, notes: e.target.value})}/></div>
              <div className="md:col-span-2 flex items-center gap-2 rounded-md border border-[var(--border)] px-3 py-2">
                <input id="accepts-marketing" type="checkbox" checked={Boolean(form.accepts_marketing)} onChange={e => setForm({...form, accepts_marketing: e.target.checked})} className="h-4 w-4 rounded border-[var(--border)] bg-transparent" />
                <label htmlFor="accepts-marketing" className="text-sm text-[var(--text)]">{lang === "ar" ? "يقبل التسويق" : "Accepts marketing"}</label>
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <button className="px-4 py-2 rounded-md border border-[var(--border)] text-[var(--text)]" onClick={() => setOpen(false)}>{t("cancel")}</button>
              <button className="btn-gold px-6 py-2 rounded-md" data-testid="customer-save-btn" onClick={submit}>{t("save")}</button>
            </div>
          </div>
        </div>
      )}

      {historyFor && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onMouseDown={(e) => onBackdropMouseDown(e, closeHistory)}>
          <div className="card-surface p-6 w-full max-w-4xl max-h-[85vh] overflow-auto space-y-4" onMouseDown={onModalPanelMouseDown} data-testid="customer-history-modal">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-heading text-xl gold-text">{lang === "ar" ? "سجل العميل" : "Customer History"}</h3>
                <p className="text-sm text-[var(--text-muted)] mt-1">{historyFor.name} {historyFor.phone ? `· ${historyFor.phone}` : ""}</p>
              </div>
              <button onClick={closeHistory} className="text-[var(--text-muted)] hover:text-[var(--text)]"><X size={18}/></button>
            </div>

            {historyLoading ? (
              <div className="text-sm text-[var(--text-muted)] py-8">{lang === "ar" ? "جاري التحميل..." : "Loading..."}</div>
            ) : historyData ? (
              <div className="space-y-5">
                {lastUsedServices.length > 0 && (
                  <div className="rounded-lg border border-[var(--border)] bg-[var(--surface)] p-4">
                    <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">{lang === "ar" ? "الخدمة الأخيرة" : "Last used service"}</div>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <div className="rounded-full border border-[var(--gold)] bg-[var(--gold-tint)] px-3 py-1 text-sm font-medium">
                        {lastUsedServices[0].name}
                      </div>
                      <span className="text-xs text-[var(--text-muted)]">{formatHistoryDate(lastUsedServices[0].date)}</span>
                      <span className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">
                        • {lastUsedServices[0].source === "invoice" ? (lang === "ar" ? "فاتورة" : "invoice") : (lang === "ar" ? "حجز" : "booking")}
                      </span>
                    </div>
                    {lastUsedServices.length > 1 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {lastUsedServices.slice(1).map((service, idx) => (
                          <div key={`${service.name}-${service.date}-${idx}`} className="rounded-full border border-[var(--border)] bg-[var(--surface)] px-2.5 py-1 text-xs text-[var(--text-muted)]">
                            {service.name}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <div className="rounded-lg border border-[var(--border)] bg-[var(--surface)] p-3">
                    <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">{lang === "ar" ? "إجمالي المشتريات" : "Total spent"}</div>
                    <div className="text-lg font-semibold gold-text mt-1">{historyData.summary.total_spent.toFixed(2)}</div>
                  </div>
                  <div className="rounded-lg border border-[var(--border)] bg-[var(--surface)] p-3">
                    <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">{lang === "ar" ? "عدد الفواتير" : "Invoices"}</div>
                    <div className="text-lg font-semibold mt-1">{historyData.summary.invoice_count}</div>
                  </div>
                  <div className="rounded-lg border border-[var(--border)] bg-[var(--surface)] p-3">
                    <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">{lang === "ar" ? "الحجوزات" : "Bookings"}</div>
                    <div className="text-lg font-semibold mt-1">{historyData.summary.booking_count}</div>
                  </div>
                  <div className="rounded-lg border border-[var(--border)] bg-[var(--surface)] p-3">
                    <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">{lang === "ar" ? "النقاط" : "Loyalty points"}</div>
                    <div className="text-lg font-semibold mt-1">{historyData.summary.loyalty_points}</div>
                  </div>
                </div>

                <div className="grid gap-4 lg:grid-cols-3">
                  <div className="rounded-lg border border-[var(--border)] bg-[var(--surface)] p-4">
                    <div className="font-semibold mb-3">{lang === "ar" ? "الفواتير" : "Invoices"}</div>
                    {historyData.invoices.length === 0 ? (
                      <div className="text-sm text-[var(--text-muted)]">{lang === "ar" ? "لا توجد فواتير" : "No invoices yet"}</div>
                    ) : (
                      <div className="space-y-3">
                        {historyData.invoices.map((inv) => (
                          <div key={inv.invoice_id || inv.invoice_number} className="rounded-md border border-[var(--border)] p-2 text-sm">
                            <div className="flex items-center justify-between gap-2">
                              <span className="font-medium">{inv.invoice_number || inv.invoice_id}</span>
                              {inv.show_financials ? (
                                <span className="gold-text">{Number(inv.customer_total ?? inv.total ?? 0).toFixed(2)}</span>
                              ) : (
                                <span className="text-[11px] text-[var(--text-muted)]">{lang === "ar" ? "سجل خدمة فقط" : "Service history only"}</span>
                              )}
                            </div>
                            {inv.show_financials === false && inv.payer_name ? (
                              <div className="text-[11px] text-[var(--text-muted)] mt-1">
                                {lang === "ar" ? "مدفوع من قبل" : "Paid by"}: {inv.payer_name}
                              </div>
                            ) : null}
                            <div className="text-[11px] text-[var(--text-muted)] mt-1">{formatHistoryDate(inv.created_at)}</div>
                            {(inv.items || []).length > 0 && (
                              <ul className="mt-2 space-y-1 text-[11px] text-[var(--text-muted)]">
                                {inv.items.map((it, idx) => (
                                  <li key={`${inv.invoice_id || inv.invoice_number}-${idx}`} className="flex flex-col gap-1 py-1">
                                    <div className="flex items-center justify-between gap-2">
                                      <span>{it.name} {it.qty > 1 ? `×${it.qty}` : ""}</span>
                                      {inv.show_financials ? (
                                        <span>{Number(it.line_total || 0).toFixed(2)}</span>
                                      ) : (
                                        <span className="text-[10px] text-[var(--text-muted)]">{lang === "ar" ? "سجل خدمة" : "Service history"}</span>
                                      )}
                                    </div>
                                    {it.recipient_customer_name ? (
                                      <div className="text-[10px] text-[var(--text-muted)]">
                                        {lang === "ar" ? "المستفيد" : "Recipient"}: {it.recipient_customer_name}
                                      </div>
                                    ) : null}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="rounded-lg border border-[var(--border)] bg-[var(--surface)] p-4">
                    <div className="font-semibold mb-3">{lang === "ar" ? "الحجوزات" : "Bookings"}</div>
                    {historyData.bookings.length === 0 ? (
                      <div className="text-sm text-[var(--text-muted)]">{lang === "ar" ? "لا توجد حجوزات" : "No bookings yet"}</div>
                    ) : (
                      <div className="space-y-3">
                        {historyData.bookings.map((bk) => (
                          <div key={bk.booking_id} className="rounded-md border border-[var(--border)] p-2 text-sm">
                            <div className="flex items-center justify-between gap-2">
                              <span className="font-medium">{bk.booking_id}</span>
                              <span className="text-[var(--text-muted)]">{bk.status || ""}</span>
                            </div>
                            <div className="text-[11px] text-[var(--text-muted)] mt-1">{formatHistoryDate(bk.created_at)}</div>
                            {(bk.items || []).length > 0 && (
                              <ul className="mt-2 space-y-1 text-[11px] text-[var(--text-muted)]">
                                {bk.items.map((it, idx) => (
                                  <li key={`${bk.booking_id}-${idx}`} className="flex items-center justify-between gap-2">
                                    <span>{it.name} {it.qty > 1 ? `×${it.qty}` : ""}</span>
                                    <span>{Number(it.price || 0).toFixed(2)}</span>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="rounded-lg border border-[var(--border)] bg-[var(--surface)] p-4">
                    <div className="font-semibold mb-3">{lang === "ar" ? "سجل الولاء" : "Loyalty history"}</div>
                    {historyData.loyalty_history.length === 0 ? (
                      <div className="text-sm text-[var(--text-muted)]">{lang === "ar" ? "لا توجد أنشطة ولاء" : "No loyalty activity yet"}</div>
                    ) : (
                      <div className="space-y-2">
                        {historyData.loyalty_history.map((evt) => (
                          <div key={evt.entry_id || evt.created_at} className="rounded-md border border-[var(--border)] p-2 text-sm">
                            <div className="flex items-center justify-between gap-2">
                              <span className="font-medium">{evt.type || "activity"}</span>
                              <span className="gold-text">{evt.points ?? evt.delta ?? 0}</span>
                            </div>
                            <div className="text-[11px] text-[var(--text-muted)] mt-1">{formatHistoryDate(evt.created_at)}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-sm text-[var(--text-muted)] py-8">{lang === "ar" ? "تعذر تحميل السجل" : "History could not be loaded"}</div>
            )}
          </div>
        </div>
      )}

      {/* Credentials modal */}
      {credFor && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onMouseDown={(e) => onBackdropMouseDown(e, closeCredentials)}>
          <div className="card-surface p-6 w-full max-w-md space-y-4" onMouseDown={onModalPanelMouseDown} data-testid="credentials-modal">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center"><Key className="text-blue-400" size={18}/></div>
                <div>
                  <h3 className="font-heading text-lg gold-text">{lang === "ar" ? "بيانات الدخول للعميل" : "Customer Login Credentials"}</h3>
                  <p className="text-xs text-[var(--text-muted)] mt-0.5">{credFor.name} {credFor.phone ? `· ${credFor.phone}` : ""}</p>
                </div>
              </div>
              <button onClick={closeCredentials} className="text-[var(--text-muted)] hover:text-[var(--text)]"><X size={18}/></button>
            </div>

            {/* Account state */}
            <div className={`rounded-md p-3 text-xs flex items-center gap-2 ${credInfo.has_password ? "bg-green-500/10 border border-green-500/30 text-green-300" : "bg-stone-500/10 border border-stone-500/30 text-stone-300"}`} data-testid="cred-status">
              {credInfo.has_password ? <ShieldCheck size={14}/> : <ShieldOff size={14}/>}
              {credInfo.has_password
                ? (lang === "ar" ? "الحساب مفعّل — يستطيع العميل تسجيل الدخول" : "Account active — customer can log in")
                : (lang === "ar" ? "الحساب غير مفعّل — لا يمكن تسجيل الدخول" : "No login set — customer cannot sign in")}
            </div>

            {/* Auto-generated password display */}
            {generatedPwd && (
              <div className="rounded-md bg-[var(--gold-tint)] border-2 border-[var(--gold)] p-3" data-testid="generated-password">
                <div className="text-[10px] uppercase tracking-wider text-[var(--gold)] mb-1.5">{lang === "ar" ? "كلمة المرور الجديدة (اعرضها للعميل الآن)" : "New password (share with customer now)"}</div>
                <div className="flex items-center justify-between gap-2">
                  <code className="font-mono text-lg gold-text font-bold tracking-wider">{generatedPwd}</code>
                  <button onClick={() => copyPwd(generatedPwd)} className="btn-gold px-3 py-1.5 rounded text-xs flex items-center gap-1.5"><Copy size={12}/>{lang === "ar" ? "نسخ" : "Copy"}</button>
                </div>
                <p className="text-[10px] text-[var(--text-muted)] mt-2">{lang === "ar" ? "لن تظهر هذه الكلمة مرة أخرى — احفظها أو شاركها مع العميل" : "This will not be shown again — save it or share with the customer"}</p>
              </div>
            )}

            {/* Form */}
            <div className="space-y-3">
              <div>
                <label className="text-xs text-[var(--text-muted)] block mb-1">{lang === "ar" ? "البريد الإلكتروني" : "Email"}</label>
                <input
                  type="email"
                  className="input-dark w-full"
                  dir="ltr"
                  placeholder="customer@example.com"
                  value={credForm.email}
                  onChange={e => setCredForm({ ...credForm, email: e.target.value })}
                  data-testid="cred-email-input"
                />
                <p className="text-[10px] text-[var(--text-muted)] mt-1">{lang === "ar" ? "البريد فريد ويمكن للعميل تسجيل الدخول به أو برقم هاتفه." : "Unique email — customer can sign in with it or phone."}</p>
              </div>
              <div>
                <label className="text-xs text-[var(--text-muted)] block mb-1">{lang === "ar" ? `كلمة المرور ${credInfo.has_password ? "(اتركها فارغة للإبقاء على الحالية)" : ""}` : `Password ${credInfo.has_password ? "(leave empty to keep current)" : ""}`}</label>
                <input
                  type="text"
                  className="input-dark w-full"
                  placeholder={lang === "ar" ? "كلمة مرور جديدة (6+ حروف)" : "New password (min 6 chars)"}
                  value={credForm.password}
                  onChange={e => setCredForm({ ...credForm, password: e.target.value })}
                  data-testid="cred-password-input"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-wrap gap-2 pt-2">
              <button
                onClick={saveCredentials}
                disabled={credSaving}
                data-testid="cred-save-btn"
                className="btn-gold px-4 py-2 rounded-md text-sm flex items-center gap-2 disabled:opacity-50"
              >
                <Check size={14}/>{lang === "ar" ? "حفظ" : "Save"}
              </button>
              <button
                onClick={resetPassword}
                disabled={credSaving}
                data-testid="cred-reset-btn"
                className="btn-rose-outline px-4 py-2 rounded-md text-sm flex items-center gap-2"
              >
                <RefreshCw size={14}/>{lang === "ar" ? "إنشاء كلمة جديدة" : "Reset (auto-gen)"}
              </button>
              {credInfo.has_password && !confirmRevoke && (
                <button
                  onClick={() => setConfirmRevoke(true)}
                  data-testid="cred-revoke-btn"
                  className="ms-auto px-3 py-2 rounded-md text-xs text-rose-300 hover:bg-rose-500/10 border border-rose-500/30 flex items-center gap-1.5"
                >
                  <ShieldOff size={12}/>{lang === "ar" ? "إلغاء صلاحية الدخول" : "Revoke login"}
                </button>
              )}
              {confirmRevoke && (
                <div className="ms-auto flex items-center gap-2 bg-rose-500/10 border border-rose-500/30 rounded-md px-2 py-1.5 text-xs text-rose-300">
                  <span>{lang === "ar" ? "تأكيد؟" : "Sure?"}</span>
                  <button onClick={revokeCredentials} disabled={credSaving} data-testid="cred-revoke-confirm" className="bg-rose-600 hover:bg-rose-700 text-white px-2 py-0.5 rounded font-semibold">{lang === "ar" ? "نعم، ألغِ" : "Yes, revoke"}</button>
                  <button onClick={() => setConfirmRevoke(false)} className="text-stone-300 hover:text-white">×</button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      <ConfirmDialogSlot />
    </div>
  );
}
